import { useCallback, useState } from 'react'
import { useStore } from 'store/store'

import { env } from '../utils/environment'

import { isMacOS, isSafari, useDevice } from './useDevice'

const ENABLE_PUSH_NOTIFICATIONS = env.VITE_PUSH_NOTIFICATION_ENABLED

export const usePushNotifications = () => {
    const { isPWA } = useDevice()
    const pushNotificationsPromptClosed = useStore(
        ({ pushNotificationsPromptClosed }) => pushNotificationsPromptClosed,
    )
    const setPushNotificationsPromptClosed = useStore(
        ({ setPushNotificationsPromptClosed }) => setPushNotificationsPromptClosed,
    )
    const permissionState = notificationsSupported() ? Notification.permission : undefined

    const [localClosed, setLocalClosed] = useState(false)

    const requestPushPermission = useCallback(async () => {
        const permission = await Notification.requestPermission()
        if (permission === 'granted' || permission === 'denied') {
            // we need to reset react state to trigger a re-render
            setLocalClosed(true)
            // not soft denied
            setPushNotificationsPromptClosed(false)
        }
    }, [setPushNotificationsPromptClosed])

    const denyPushPermission = useCallback(() => {
        setPushNotificationsPromptClosed(true)
    }, [setPushNotificationsPromptClosed])

    const simplifiedPermissionState: 'granted' | 'soft-denied' | 'hard-denied' | 'default' =
        permissionState === 'granted'
            ? 'granted'
            : permissionState === 'denied'
              ? 'hard-denied'
              : pushNotificationsPromptClosed
                ? 'soft-denied'
                : 'default'

    const displayNotificationBanner =
        !localClosed &&
        (ENABLE_PUSH_NOTIFICATIONS || isPWA) &&
        simplifiedPermissionState === 'default' &&
        !(isSafari() && isMacOS() && isPWA) &&
        notificationsSupported()

    return {
        requestPushPermission,
        denyPushPermission,
        simplifiedPermissionState,
        displayNotificationBanner,
    }
}

export function notificationsSupported(): boolean {
    return typeof Notification !== 'undefined'
}
